import { SUPPORTED_CHAIN_IDS, isSupportedChainId } from '@eulerxyz/core-sdk'
import {
  AccountAggregator,
  EvcBatchSimulationAggregator,
  type EvcBatchSimulationAggregatorArgs,
  OracleBatchItemFactory,
  PriceAggregator,
  ResultEulerEarnVaultAggregator,
  ResultVaultAggregator,
  VaultAggregator,
} from '@eulerxyz/evc-sdk'
import type { Transport } from '@wagmi/core'
import { getConnectorClient } from '@wagmi/core'
import { type Account, type Address, type Client, getAddress, zeroAddress } from 'viem'
import type { Chain } from 'viem/chains'
import type { Config } from 'wagmi'
import { ChainNotConfiguredError } from 'wagmi'
import type { ExtendedConfig } from './types'

export function extendConfig<
  chains extends readonly [Chain, ...Chain[]] = readonly [Chain, ...Chain[]],
  account extends Account | undefined = Account | undefined,
  transports extends Record<chains[number]['id'], Transport> = Record<chains[number]['id'], Transport>,
>(config: Config): ExtendedConfig<chains, account, transports> {
  // @ts-ignore - Type constraint mismatch with Chain
  const clients = new Map<`${number}:${Address}`, EvcBatchSimulationAggregator<chains[number]>>()

  const vaultAggregator = new Map<number, VaultAggregator<ResultVaultAggregator>>(
    SUPPORTED_CHAIN_IDS.map(
      (chainId) => [chainId, new VaultAggregator<ResultVaultAggregator>(chainId, 'classic')] as const,
    ),
  )
  const managedVaultAggregator = new Map<number, VaultAggregator<ResultEulerEarnVaultAggregator>>(
    SUPPORTED_CHAIN_IDS.map(
      (chainId) => [chainId, new VaultAggregator<ResultEulerEarnVaultAggregator>(chainId, 'managed')] as const,
    ),
  )
  // @ts-ignore - Type constraint mismatch with Account
  const accountAggregator = new Map<number, AccountAggregator<account>>()
  const priceAggregator = new Map<number, PriceAggregator>(
    SUPPORTED_CHAIN_IDS.map((chainId) => [chainId, new PriceAggregator(chainId)] as const),
  )
  const oracleBatchFactory = new Map<number, OracleBatchItemFactory>(
    SUPPORTED_CHAIN_IDS.map((chainId) => [chainId, new OracleBatchItemFactory(chainId)] as const),
  )

  // @ts-ignore - Type constraint mismatch with Chain
  async function getEvcClient(
    // @ts-ignore - Type constraint mismatch with Chain
    parameters: EvcBatchSimulationAggregatorArgs<chains[number]> & {
      chainId?: chains[number]['id'] | undefined
      newInstance?: boolean
    } = {},
    // @ts-ignore - Type constraint mismatch with Chain
  ): Promise<EvcBatchSimulationAggregator<chains[number]>> {
    const { chainId: _chainId, newInstance, ...rest } = parameters
    const chainId = _chainId ?? config.state.chainId
    // @ts-ignore - Type constraint mismatch with Chain
    const chain = config.chains.find((x) => x.id === chainId)
    // chainId specified and not configured
    if (parameters.chainId && !chain) throw new ChainNotConfiguredError()

    // @ts-ignore - Type constraint mismatch with Chain
    let publicClient: Client<transports[chains[number]['id']], chains[number]>

    const _account =
      typeof rest.account === 'string'
        ? getAddress(rest.account)
        : rest.account
          ? getAddress(rest.account.address)
          : undefined

    if (config.state.status === 'connected' && _account !== zeroAddress) {
      publicClient = await getConnectorClient(config, {
        chainId,
      })
    } else {
      publicClient = config.getClient({ chainId })
    }

    const account = _account || publicClient.account?.address || zeroAddress

    if (!newInstance) {
      {
        const client = clients.get(`${chainId}:${account}`)
        // @ts-ignore - Type constraint mismatch with Chain
        client?.setArgs(rest as EvcBatchSimulationAggregatorArgs<chains[number]>)
        if (client && !chain) return client
        else if (!chain) throw new ChainNotConfiguredError()
      }

      {
        const client = clients.get(`${chainId}:${account}`)
        // @ts-ignore - Type constraint mismatch with Chain
        client?.setArgs(rest as EvcBatchSimulationAggregatorArgs<chains[number]>)
        if (client) return client
      }
    }

    // @ts-ignore - Type constraint mismatch with Chain
    const evc = new EvcBatchSimulationAggregator<chains[number]>(
      publicClient,
      // @ts-ignore - Type constraint mismatch with Chain
      rest as EvcBatchSimulationAggregatorArgs<chains[number]>,
    )

    if (!newInstance) {
      clients.set(`${chainId}:${account}`, evc)
    }
    return evc
  }

  async function getVaultAggregator(
    parameters: {
      chainId?: chains[number]['id'] | undefined
    } = {},
  ): Promise<VaultAggregator<ResultVaultAggregator>> {
    const { chainId } = parameters
    if (!chainId || !isSupportedChainId(chainId)) throw new ChainNotConfiguredError()
    let _vaultAggregator = vaultAggregator.get(chainId)
    if (!_vaultAggregator) {
      _vaultAggregator = new VaultAggregator<ResultVaultAggregator>(chainId)
      vaultAggregator.set(chainId, _vaultAggregator)
    }
    return _vaultAggregator
  }

  async function getManagedVaultAggregator(
    parameters: {
      chainId?: chains[number]['id'] | undefined
    } = {},
  ): Promise<VaultAggregator<ResultEulerEarnVaultAggregator>> {
    const { chainId } = parameters
    if (!chainId || !isSupportedChainId(chainId)) throw new ChainNotConfiguredError()
    let _managedVaultAggregator = managedVaultAggregator.get(chainId)
    if (!_managedVaultAggregator) {
      _managedVaultAggregator = new VaultAggregator<ResultEulerEarnVaultAggregator>(chainId)
      managedVaultAggregator.set(chainId, _managedVaultAggregator)
    }
    return _managedVaultAggregator
  }

  // @ts-ignore - Type constraint mismatch with Account
  async function getAccountAggregator(
    parameters: {
      chainId?: chains[number]['id'] | undefined
    } = {},
    // @ts-ignore - Type constraint mismatch with Account
  ): Promise<AccountAggregator<account>> {
    const { chainId: _chainId } = parameters
    const chainId = _chainId ?? config.state.chainId
    const chain = config.chains.find((x) => x.id === chainId)
    // chainId specified and not configured
    if (parameters.chainId && !chain) throw new ChainNotConfiguredError()
    const client = config.getClient({ chainId }) as Client<transports[chains[number]['id']], chains[number], account>

    {
      const aggregator = accountAggregator.get(chainId)
      if (aggregator && !chain) return aggregator
      else if (!chain) throw new ChainNotConfiguredError()
    }

    {
      const aggregator = accountAggregator.get(chainId)
      if (aggregator) return aggregator
    }

    // @ts-ignore - Type constraint mismatch with Account
    const aggregator = new AccountAggregator<account>(client)
    accountAggregator.set(chainId, aggregator)

    return aggregator
  }

  function getOracleBatchItemFactory(
    parameters: {
      chainId?: chains[number]['id'] | undefined
      pythHermesEndpoint?: string | undefined
    } = {},
  ) {
    const { chainId, pythHermesEndpoint } = parameters
    if (!chainId) throw new ChainNotConfiguredError()
    let oracleFactory = oracleBatchFactory.get(chainId)
    if (!oracleFactory) {
      oracleFactory = new OracleBatchItemFactory(chainId, pythHermesEndpoint)
      oracleBatchFactory.set(chainId, oracleFactory)
    }
    return oracleFactory
  }

  function getPriceAggregator(
    parameters: {
      chainId?: chains[number]['id'] | undefined
    } = {},
  ) {
    const { chainId } = parameters
    if (!chainId || !isSupportedChainId(chainId)) throw new ChainNotConfiguredError()
    let _priceAggregator = priceAggregator.get(chainId)
    if (!_priceAggregator) {
      _priceAggregator = new PriceAggregator(chainId)
      priceAggregator.set(chainId, _priceAggregator)
    }
    return _priceAggregator
  }

  return Object.assign(config, {
    getEvcClient,
    getVaultAggregator,
    getManagedVaultAggregator,
    getAccountAggregator,
    getOracleBatchItemFactory,
    getPriceAggregator,
    // @ts-ignore - Type constraint mismatch
  }) as ExtendedConfig<chains, account, transports>
}
